import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { CheckCircle, Circle, CircleNotch, Warning, ArrowRight, Hourglass, Broadcast } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  done: { icon: CheckCircle, cls: "text-emerald-600", line: "bg-emerald-600", label: "Complete", badge: "bg-emerald-700" },
  action: { icon: Warning, cls: "text-amber-600", line: "bg-slate-200", label: "Action needed", badge: "bg-amber-600" },
  processing: { icon: CircleNotch, cls: "text-blue-600 animate-spin", line: "bg-slate-200", label: "Processing", badge: "bg-blue-700" },
  in_progress: { icon: Hourglass, cls: "text-slate-500", line: "bg-slate-200", label: "In progress", badge: "bg-slate-600" },
  pending: { icon: Circle, cls: "text-slate-300", line: "bg-slate-200", label: "Not started", badge: "bg-slate-400" },
  upcoming: { icon: Broadcast, cls: "text-slate-300", line: "bg-slate-200", label: "Coming soon", badge: "bg-slate-400" },
};

export default function JourneyPage() {
  const { productId, product } = useProduct();
  const [j, setJ] = useState(null);

  useEffect(() => {
    setJ(null);
    axios.get(`${API}/journey/${productId}`).then((r) => setJ(r.data)).catch(console.error);
  }, [productId]);

  if (!j) return (
    <PageShell pageId="journey" overline="Guided Strategy Workspace" title="Strategy Workspace">
      <div className="py-32 text-center text-slate-400 text-sm">Deriving journey state…</div>
    </PageShell>
  );

  return (
    <PageShell pageId="journey" overline={`Guided Strategy Workspace · ${product?.name || ""}`} title="Strategy Workspace">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div data-testid="journey-progress-card" className="bg-slate-900 text-white p-7">
          <p className="overline-label text-white/50">Your Progress · {j.product.name}</p>
          <p className="font-heading font-extrabold leading-none mt-4" style={{ fontSize: "56px" }}>
            {j.progress.done}<span className="text-2xl text-white/40">/{j.progress.total} stages</span>
          </p>
          <div className="h-2 bg-white/10 mt-5"><div className="h-full bg-emerald-500" style={{ width: `${j.progress.pct}%` }} /></div>
          <p className="font-mono-data text-[11px] text-white/50 mt-2">{j.progress.pct}% complete · GMARI™ {j.gmari.score}/100 · {j.gmari.band}</p>
          {j.next_step && (
            <div className="mt-6 border-t border-white/10 pt-5">
              <p className="overline-label text-emerald-400">Do this next</p>
              <p className="text-sm font-semibold mt-1.5">{j.next_step.label}</p>
              <p className="text-xs text-white/60 leading-relaxed mt-1">{j.next_step.detail}</p>
              {j.next_step.cta && (
                <Link data-testid="journey-next-cta" to={j.next_step.cta.to}
                  className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold px-4 py-2.5 mt-4">
                  {j.next_step.cta.label} <ArrowRight size={12} weight="bold" />
                </Link>
              )}
            </div>
          )}
        </div>

        <div className="xl:col-span-2 bg-white border border-slate-200">
          <div className="px-5 py-4 border-b border-slate-200">
            <p className="font-heading font-bold text-sm tracking-tight">The Journey — from workspace to global strategy</p>
            <p className="text-xs text-slate-500 mt-1">Progress is derived from live data — stages complete themselves as work lands in the platform.</p>
          </div>
          <div className="px-5 py-5">
            {j.stages.map((st, i) => {
              const m = STATUS_META[st.status] || STATUS_META.pending;
              const Icon = m.icon;
              return (
                <div key={st.id} data-testid={`journey-stage-${st.id}`} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <Icon size={20} weight={st.status === "done" ? "fill" : "regular"} className={m.cls} />
                    {i < j.stages.length - 1 && <div className={`w-px flex-1 my-1 ${st.status === "done" ? "bg-emerald-200" : "bg-slate-200"}`} />}
                  </div>
                  <div className={`pb-6 flex-1 min-w-0 ${i === j.stages.length - 1 ? "pb-1" : ""}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-900">{st.label}</p>
                      <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white ${m.badge}`}>{m.label}</span>
                    </div>
                    <p className="text-xs text-slate-500 leading-relaxed mt-1">{st.detail}</p>
                    {st.cta && st.status !== "done" && st.status !== "upcoming" && (
                      <Link data-testid={`journey-cta-${st.id}`} to={st.cta.to}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-900 border border-slate-300 px-3 py-1.5 mt-2 hover:bg-slate-50">
                        {st.cta.label} <ArrowRight size={11} weight="bold" />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
